// src/data/questions/aiQuestions.ts
// AI/ML and 2026 Updates Questions (Q41-Q50)

import { Question } from '../../types';

export const aiQuestions: Question[] = [
  {
    id: 'Q41',
    section: 'ai',
    question: 'Which AI/ML vendor(s) do you use? (Select all that apply)',
    type: 'multiple',
    options: [
      { value: 'bedrock', label: 'AWS Bedrock', description: '', points: 0 },
      { value: 'openai', label: 'OpenAI (ChatGPT, GPT-4)', description: '', points: 0 },
      { value: 'google', label: 'Google Vertex AI / Gemini', description: '', points: 0 },
      { value: 'azure', label: 'Microsoft Azure OpenAI', description: '', points: 0 },
      { value: 'anthropic', label: 'Anthropic Claude (API)', description: '', points: 0 },
      { value: 'selfhosted', label: 'Self-hosted open-source models', description: '', points: 0 },
      { value: 'other', label: 'Other vendor', description: '', points: 0 },
      { value: 'custom', label: 'Custom-built models', description: '', points: 0 },
    ],
    regulation: 'AI vendor profile',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 1,
    tooltipContent: {
      plainEnglish: 'This question identifies your AI/ML vendor ecosystem. Different vendors have different HIPAA compliance postures, BAA availability, and data use policies. Some vendors (AWS Bedrock, Azure OpenAI, Google Vertex AI) have signed HIPAA BAAs and are designed for healthcare use. Others (OpenAI ChatGPT, free tier models, self-hosted models) may not be HIPAA-compliant or may allow data to be used for model training.',
      hipaaReference: '§164.308(a)(1)(ii)(A) - Risk Analysis; AI/ML creates unique risks requiring assessment',
      realWorldExample: 'A clinic uses ChatGPT to help clinicians draft clinical notes by providing suggestions. They copy/paste patient data into ChatGPT. OpenAI uses this data to train its models. Automatic violation (data sent to non-HIPAA vendor without BAA). Fix: use Azure OpenAI instead, which has a HIPAA BAA and enterprise agreement.',
      estimatedCost: {
        small: '$5,000-15,000',
        medium: '$15,000-35,000', 
        large: '$35,000-65,000'
      },
      implementationSteps: [
        'Conduct vendor assessment (2-4 weeks)',
        'Negotiate BAAs with compliant vendors (2-6 weeks)',
        'Migrate from non-compliant vendors if necessary (4-12 weeks)',
        'Document vendor compliance status'
      ],
      commonMistakes: [
        'Using consumer AI tools (ChatGPT free) with patient data',
        'Assuming all AI vendors are HIPAA-compliant',
        'Not verifying BAA availability before implementation',
        'Mixing compliant and non-compliant vendors without proper controls'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '2-4 weeks',
        medium: '4-8 weeks',
        large: '8-16 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT Director', 'AI/ML team', 'Compliance Officer'],
      whereToLook: ['Vendor contracts', 'AI system documentation', 'Data processing agreements'],
      howToVerify: 'Review all AI/ML tools in use. Check vendor websites for HIPAA BAA availability. Document which vendors handle PHI vs. non-PHI data.',
      whatToDocument: 'List of all AI vendors, BAA status, data handling policies, migration plan for non-compliant vendors'
    }
  },
  {
    id: 'Q42',
    section: 'ai',
    question: 'Do you have signed BAAs with your AI/ML vendors?',
    type: 'single',
    options: [
      { value: 'all', label: 'Yes, BAAs with all AI/ML vendors', description: '', points: 3 },
      { value: 'some', label: 'Some vendors, not all', description: '', points: 1 },
      { value: 'no', label: 'No BAAs with AI vendors', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure if AI vendors need BAAs', description: '', points: 0 },
    ],
    regulation: 'AI vendor BAA requirements (AWS Bedrock, Azure OpenAI, etc.)',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 3,
    tooltipContent: {
      plainEnglish: 'If you use AI/ML for healthcare purposes and the vendor handles PHI, you must have a signed BAA (Business Associate Agreement). The BAA specifies the vendor\'s security obligations, data handling rules, breach notification procedures, and right to audit. A HIPAA BAA for AI includes additional clauses around data retention (no training on your data), algorithm transparency, and audit rights.',
      hipaaReference: '§164.308(b)(1) - Business Associate Contracts; applies to all vendors handling PHI including AI/ML vendors',
      realWorldExample: 'A hospital wants to use an AI vendor for diagnostic assistance. They request a HIPAA BAA. The vendor provides a standard BAA but includes language saying "vendor may use anonymized data for model training." Hospital negotiates to remove this clause, adding "vendor must not use any data, anonymized or not, for model training or product improvement." Vendor agrees. BAA signed; compliant.',
      estimatedCost: {
        small: '$1,000-8,000',
        medium: '$8,000-20,000',
        large: '$20,000-55,000'
      },
      implementationSteps: [
        'Request BAA from all AI vendors (2-6 weeks)',
        'Legal review of BAA terms (1-2 weeks)',
        'Negotiate zero-retention and no-training clauses',
        'Document and track BAA expiration dates (1 week)'
      ],
      commonMistakes: [
        'Accepting BAAs that allow data training',
        'Not tracking BAA expiration dates',
        'Using AI vendors without BAAs',
        'Assuming all vendors will provide BAAs'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '2-6 weeks',
        medium: '4-8 weeks',
        large: '6-12 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Legal team', 'Procurement', 'AI vendor contacts', 'Compliance Officer'],
      whereToLook: ['Vendor contracts', 'BAA repository', 'Vendor websites', 'Legal documentation'],
      howToVerify: 'List all AI vendors. Check which ones handle PHI. Request BAAs from vendors handling PHI. Review BAA terms for data training prohibitions.',
      whatToDocument: 'BAA status for each vendor, renewal dates, key terms, negotiation outcomes'
    }
  },
  {
    id: 'Q43',
    section: 'ai',
    question: 'Do you log all AI prompts and responses containing PHI?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, comprehensive logging with 6-year retention', description: '', points: 3 },
      { value: 'partial', label: 'Partial logging', description: '', points: 1 },
      { value: 'no', label: 'No logging of AI interactions', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(b) - Audit controls for AI systems',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 3,
    tooltipContent: {
      plainEnglish: 'Every prompt (input) and response (output) involving PHI must be logged. This enables: (1) audit trail for investigations, (2) detection of misuse, (3) compliance verification, and (4) breach investigation. Logging requirements are the same as for EHR access: who (which user/clinician), what (which patient), when (timestamp), where (which system), why (purpose of access). Logs must be retained for 6 years per HIPAA standards.',
      hipaaReference: '§164.312(b) - Audit Controls; requires logging all access to ePHI including AI system access',
      realWorldExample: 'A hospital uses Azure OpenAI for diagnostic suggestions. They configure logging: every prompt (which clinician, patient ID, prompt text) and response (AI output, timestamp, model used) is logged to Azure Monitor. Logs are retained for 7 years (beyond HIPAA\'s 6-year minimum). When OCR audits, they can show comprehensive AI logging.',
      estimatedCost: {
        small: '$2,000-7,000',
        medium: '$7,000-15,000',
        large: '$15,000-30,000'
      },
      implementationSteps: [
        'Assess logging capabilities (1-2 weeks)',
        'Implement logging solution (2-4 weeks)',
        'Configure log retention for 6+ years',
        'Test and validate logging (1 week)'
      ],
      commonMistakes: [
        'Not logging AI interactions at all',
        'Logging only prompts but not responses',
        'Insufficient log retention periods',
        'Not including user identification in logs'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '2-4 weeks',
        medium: '4-6 weeks',
        large: '6-10 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT team', 'AI platform administrators', 'Security team', 'Compliance Officer'],
      whereToLook: ['AI platform settings', 'Log management systems', 'Vendor documentation', 'Security policies'],
      howToVerify: 'Check if AI vendor provides logging. If not, implement application-level logging. Verify logs include user, patient, timestamp, prompt, response.',
      whatToDocument: 'Logging configuration, retention policies, log samples, audit trail procedures'
    }
  },
  {
    id: 'Q44',
    section: 'ai',
    question: 'How do you ensure AI models don\'t train on your PHI?',
    type: 'single',
    options: [
      { value: 'contractual', label: 'Vendor contractual guarantees + technical controls (zero retention)', description: '', points: 3 },
      { value: 'vendoronly', label: 'Vendor guarantees only', description: '', points: 2 },
      { value: 'unsure', label: 'We\'re not sure if our data is used for training', description: '', points: 0 },
      { value: 'trains', label: 'Our vendor does train on our data', description: '', points: 0 },
    ],
    regulation: 'AI data usage and model training restrictions',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 3,
    tooltipContent: {
      plainEnglish: 'Many AI vendors use customer data to improve their models (e.g., OpenAI trains on ChatGPT conversations). For healthcare, this is strictly prohibited: patient data cannot be used to train AI models. You must have contractual guarantees (via BAA) and technical controls to prevent training on your data. Contractual guarantee: BAA explicitly states "vendor will not use customer data for model training." Technical controls: zero retention where data is immediately deleted after response generation.',
      hipaaReference: '§164.308(a)(1)(ii)(A) - Risk Analysis; using third-party AI with data training is a high-risk violation',
      realWorldExample: 'A clinic uses Azure OpenAI with an enterprise agreement that explicitly prohibits using their data for training. Additionally, they configure retention policies to "zero retention" so data is deleted immediately after the AI generates a response. Doubly protected: contractual + technical.',
      estimatedCost: {
        small: '$5,000-15,000',
        medium: '$15,000-35,000',
        large: '$35,000-65,000'
      },
      implementationSteps: [
        'Verify contractual no-training guarantees (1-2 weeks)',
        'Implement technical controls like zero retention (2-4 weeks)',
        'Obtain vendor attestation of compliance (1-2 weeks)',
        'Regular audits to verify no-training commitment'
      ],
      commonMistakes: [
        'Using vendors that train on customer data',
        'Relying only on contractual guarantees without technical controls',
        'Not verifying vendor compliance regularly',
        'Accepting "anonymized data training" clauses'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '1-3 weeks',
        medium: '3-6 weeks',
        large: '6-12 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['AI vendor contacts', 'Legal team', 'IT security team', 'Compliance Officer'],
      whereToLook: ['Vendor contracts', 'BAA terms', 'Platform settings', 'Data processing agreements'],
      howToVerify: 'Review all AI vendor contracts for data training clauses. Check vendor settings for zero retention. Obtain written attestation that your data is not used for training.',
      whatToDocument: 'Contractual guarantees, technical controls, vendor attestations, compliance verification procedures'
    }
  },
  {
    id: 'Q45',
    section: 'ai',
    question: 'Do you have processes to detect and prevent PHI leakage in AI prompts?',
    type: 'single',
    options: [
      { value: 'automated', label: 'Yes, automated scanning + human review', description: '', points: 3 },
      { value: 'manual', label: 'Manual review only', description: '', points: 2 },
      { value: 'no', label: 'No controls to prevent PHI in prompts', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Data leakage prevention for AI',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 2,
    tooltipContent: {
      plainEnglish: 'Clinicians often accidentally send PHI to AI systems—full patient records, medication lists, medical histories. You need: (1) awareness training, (2) technical controls (automatically detect PHI in prompts before sending to vendor), and (3) monitoring (track AI usage for suspicious patterns). Technical controls might include: regex pattern matching, machine learning classifiers, or manual review workflows.',
      hipaaReference: '§164.502(b) - Minimum Necessary; applies to data sent to AI systems; §164.312(a) - Access Controls',
      realWorldExample: 'A clinic implements a DLP policy: any prompt containing patterns matching a SSN, full name + date of birth, or medical record number is flagged. Clinician tries to send full patient record to AI for diagnosis suggestion; DLP blocks it and warns the clinician. Clinician then sends a de-identified summary (age, symptoms only) which is allowed.',
      estimatedCost: {
        small: '$5,000-15,000',
        medium: '$15,000-35,000',
        large: '$35,000-65,000'
      },
      implementationSteps: [
        'Assess and select DLP tools (2-3 weeks)',
        'Implement automated scanning (2-4 weeks)',
        'Train clinicians on AI usage policies (1-2 weeks)',
        'Set up monitoring and alerts'
      ],
      commonMistakes: [
        'No controls to prevent PHI in prompts',
        'Relying only on training without technical controls',
        'Not monitoring AI usage patterns',
        'Allowing full patient records in AI prompts'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '3-6 weeks',
        medium: '6-10 weeks',
        large: '10-16 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT security team', 'Clinical staff', 'DLP administrators', 'Compliance Officer'],
      whereToLook: ['AI usage logs', 'DLP policies', 'Training records', 'Security monitoring systems'],
      howToVerify: 'Review current AI usage patterns. Check if clinicians send full patient records to AI. Implement DLP scanning for PHI patterns.',
      whatToDocument: 'DLP policies, training completion, monitoring alerts, usage patterns, violation incidents'
    }
  },
  {
    id: 'Q46',
    section: 'ai',
    question: 'Do you provide transparency/explainability for AI clinical decisions?',
    type: 'single',
    options: [
      { value: 'documented', label: 'Yes, documented decision logic + audit trails', description: '', points: 3 },
      { value: 'partial', label: 'Partial transparency', description: '', points: 1 },
      { value: 'no', label: 'No explainability mechanisms', description: '', points: 0 },
      { value: 'na', label: 'Not applicable (non-clinical AI use)', description: '', points: 3 },
    ],
    regulation: 'State AI transparency laws (NY S.7803, CO SB 21-169)',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 2,
    tooltipContent: {
      plainEnglish: 'State laws increasingly require transparency about AI use in healthcare. If you use AI to assist in clinical decisions, you must explain: what the AI does, how it reaches decisions, how it influences clinical care, and give patients the right to know when AI is involved. For example: "An AI system analyzes your test results and suggests possible diagnoses. A clinician reviews and confirms the diagnosis before treatment."',
      hipaaReference: 'California AB 2013 (autonomous systems), New York S.7803 (algorithmic accountability), Colorado SB 21-169 (AI transparency), Illinois BIPA (biometric privacy)',
      realWorldExample: 'A hospital uses AI for diabetic retinopathy screening in eye images. They create a patient consent form: "An AI system will analyze your eye images to screen for signs of diabetic retinopathy. A clinician will review the results and discuss with you. The AI assists but does not replace clinical judgment." Consent is obtained before using AI. Compliant.',
      estimatedCost: {
        small: '$3,000-8,000',
        medium: '$8,000-18,000',
        large: '$18,000-35,000'
      },
      implementationSteps: [
        'Update consent forms and NPP (2-3 weeks)',
        'Create patient-facing explanation materials (2-3 weeks)',
        'Document model explainability (varies)',
        'Implement and train staff (1-2 weeks)'
      ],
      commonMistakes: [
        'Not disclosing AI use to patients',
        'Providing vague explanations of AI functionality',
        'Not documenting AI decision-making processes',
        'Ignoring state-specific transparency requirements'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '4-6 weeks',
        medium: '6-10 weeks',
        large: '10-16 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Legal team', 'Clinical staff', 'Patient experience team', 'Compliance Officer'],
      whereToLook: ['State regulations', 'Consent forms', 'AI system documentation', 'Patient communications'],
      howToVerify: 'Review state laws that apply to your organization. Check if AI use is disclosed to patients. Update consent forms to explain AI involvement.',
      whatToDocument: 'State law compliance matrix, updated consent forms, patient disclosure materials, AI decision processes'
    }
  },
  {
    id: 'Q47',
    section: 'ai',
    question: 'Do you have human-in-the-loop oversight for AI clinical decisions?',
    type: 'single',
    options: [
      { value: 'all', label: 'Yes, all AI outputs reviewed by clinicians', description: '', points: 3 },
      { value: 'partial', label: 'Partial oversight', description: '', points: 1 },
      { value: 'automated', label: 'Fully automated AI decisions', description: '', points: 0 },
      { value: 'na', label: 'Not applicable', description: '', points: 3 },
    ],
    regulation: 'Responsible AI practices',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 2,
    tooltipContent: {
      plainEnglish: 'AI in healthcare should not make autonomous clinical decisions; clinicians must review and approve. "Human-in-the-loop" means: AI provides recommendations, a human clinician evaluates the recommendations in context of the patient\'s full situation, and the clinician makes the final decision. Fully automated AI decisions (without human review) are inappropriate for clinical care because AI can make mistakes, miss context, or have biases.',
      hipaaReference: 'Colorado SB 21-169 (AI transparency and human review); FDA guidance on AI in medical devices (human oversight expected); State medical board guidance',
      realWorldExample: 'A hospital uses AI to flag patients at high risk of sepsis. The AI alerts clinicians but doesn\'t automatically order treatment. Clinician reviews the alert, evaluates the patient\'s symptoms, and decides whether to escalate care. Human decision-maker. Compliant.',
      estimatedCost: {
        small: '$7,000-20,000',
        medium: '$20,000-45,000',
        large: '$45,000-85,000'
      },
      implementationSteps: [
        'Analyze and redesign workflows (2-4 weeks)',
        'Implement human review checkpoints (2-4 weeks)',
        'Add explainability features (varies)',
        'Train clinicians on AI oversight (1-2 weeks)'
      ],
      commonMistakes: [
        'Allowing fully automated AI decisions',
        'Not providing explainability for AI recommendations',
        'Insufficient clinician training on AI limitations',
        'Not documenting human review decisions'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '4-8 weeks',
        medium: '8-12 weeks',
        large: '12-20 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Clinical staff', 'AI system administrators', 'Medical directors', 'Compliance Officer'],
      whereToLook: ['AI system workflows', 'Clinical protocols', 'Decision support systems', 'Training materials'],
      howToVerify: 'Review all AI systems for autonomous decision-making. Ensure clinicians review AI recommendations before acting. Add explainability features.',
      whatToDocument: 'Workflow designs, human review checkpoints, explainability features, training records, decision documentation'
    }
  },
  {
    id: 'Q48',
    section: 'updates',
    question: 'Are you aware of the January 2025 HIPAA Security Rule updates?',
    type: 'single',
    options: [
      { value: 'implemented', label: 'Yes, and we\'ve implemented required changes', description: '', points: 3 },
      { value: 'aware', label: 'Aware, but not yet compliant', description: '', points: 1 },
      { value: 'unaware', label: 'Not aware of the updates', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '2026 regulatory changes',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: 'The HHS OCR released final HIPAA Security Rule updates in January 2025, effective date anticipated for January 2026. Key updates include: (1) mandatory MFA for all users (no longer addressable), (2) network segmentation emphasis, (3) updated encryption standards, (4) enhanced incident response requirements, and (5) risk analysis/management emphasis. Organizations must implement these by the effective date or face enforcement action.',
      hipaaReference: '2026 HIPAA Security Rule Update (Federal Register, January 2025 final rule)',
      realWorldExample: 'A hospital reads the January 2025 HIPAA Security Rule update in January 2025. They immediately begin implementing MFA (due by January 2026), assess network segmentation, and update their risk analysis. By December 2025, they\'re compliant with the new requirements. OCR audit in 2026 finds no issues.',
      estimatedCost: {
        small: '$40,000-75,000',
        medium: '$75,000-150,000',
        large: '$150,000-300,000+'
      },
      implementationSteps: [
        'Awareness and assessment (4-8 weeks)',
        'MFA implementation (8-16 weeks)',
        'Network segmentation (12-24 weeks)',
        'Risk analysis update (4-8 weeks)'
      ],
      commonMistakes: [
        'Ignoring updates until effective date',
        'Not starting MFA implementation early',
        'Underestimating network segmentation complexity',
        'Not updating risk analysis for new requirements'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '12-18 months',
        medium: '18-24 months',
        large: '24-36 months'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT security team', 'Compliance Officer', 'Legal team', 'Network administrators'],
      whereToLook: ['Federal Register', 'HHS OCR updates', 'Current security controls', 'Risk analysis documents'],
      howToVerify: 'Review the January 2025 final rule. Assess current MFA implementation. Evaluate network segmentation. Update risk analysis.',
      whatToDocument: 'Implementation timeline, current state assessment, gap analysis, compliance roadmap'
    }
  },
  {
    id: 'Q49',
    section: 'updates',
    question: 'Are you prepared for HHS compliance audits (resumed December 2024)?',
    type: 'single',
    options: [
      { value: 'ready', label: 'Yes, audit-ready with documented evidence', description: '', points: 3 },
      { value: 'partial', label: 'Partially prepared', description: '', points: 1 },
      { value: 'no', label: 'Not prepared for audits', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'HHS audit protocol',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: 'OCR resumed HIPAA compliance audits in December 2024 after a multi-year pause. Organizations should be prepared for potential OCR contact. Preparedness includes: (1) organized documentation, (2) quick access to policies and evidence of compliance, (3) designated audit coordinator, and (4) legal counsel available if needed. An OCR audit is formal and can last weeks or months.',
      hipaaReference: 'OCR Audit Authority (§164.308 and throughout HIPAA Rule); OCR announcement of audit resumption (December 2024)',
      realWorldExample: 'A hospital prepares for potential OCR audit by: (1) organizing all policies in a shared drive, (2) confirming training records are accessible, (3) verifying all BAAs are signed and current, (4) preparing a HIPAA program summary, (5) designating an audit coordinator. OCR contacts them for audit; they respond within 5 days. OCR finds two minor gaps; hospital remediates within 30 days. Positive outcome.',
      estimatedCost: {
        small: '$15,000-35,000',
        medium: '$35,000-65,000',
        large: '$65,000-125,000+'
      },
      implementationSteps: [
        'Audit readiness assessment (2-4 weeks)',
        'Documentation organization (4-8 weeks)',
        'Mock audit/self-assessment (4-6 weeks)',
        'Remediation of gaps found (4-12 weeks)'
      ],
      commonMistakes: [
        'Scattered, disorganized documentation',
        'Outdated or incomplete risk analysis',
        'Missing or expired BAAs',
        'No designated audit coordinator'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '8-12 weeks',
        medium: '12-18 weeks',
        large: '18-26 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Compliance Officer', 'Legal team', 'IT security team', 'HR team'],
      whereToLook: ['Policy repository', 'Training records', 'BAA files', 'Risk analysis documents'],
      howToVerify: 'Organize all HIPAA documentation. Verify policies are current. Check BAA status. Prepare audit coordinator.',
      whatToDocument: 'Audit readiness checklist, organized documentation, audit coordinator designation, legal counsel contact'
    }
  },
  {
    id: 'Q50',
    section: 'updates',
    question: 'Are you compliant with state AI laws (CA, CO, NY, IL)?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, compliant with all applicable state laws', description: '', points: 3 },
      { value: 'partial', label: 'Partial compliance', description: '', points: 1 },
      { value: 'no', label: 'Not compliant', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure which laws apply', description: '', points: 0 },
    ],
    regulation: 'State AI disclosure and transparency laws',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: ['ai', 'multistate'] },
    weight: 2,
    tooltipContent: {
      plainEnglish: 'Four states have AI-specific laws relevant to healthcare: California AB 2013 (autonomous systems in insurance), Colorado SB 21-169 (AI transparency), New York S.7803 (algorithmic accountability), and Illinois BIPA (biometric privacy). If you operate in any of these states or serve patients from these states, you must comply. These laws often have requirements beyond HIPAA (e.g., transparency, explainability, bias testing, human review).',
      hipaaReference: 'California AB 2013, Colorado SB 21-169, New York S.7803, Illinois BIPA (Public Act 91-0142)',
      realWorldExample: 'A clinic operates in California, Colorado, and New York. They use an AI system for diagnostic support. Compliance requirements: CA AB 2013 (autonomous system transparency), CO SB 21-169 (AI transparency and human review), NY S.7803 (algorithm transparency and bias testing). Clinic implements: transparency disclosure to patients, human clinician review for AI decisions, documentation of model bias testing, consent forms explaining AI use. Compliant with all three states.',
      estimatedCost: {
        small: '$15,000-30,000',
        medium: '$30,000-60,000',
        large: '$60,000-120,000'
      },
      implementationSteps: [
        'State AI law assessment (2-3 weeks)',
        'Policy and documentation (3-4 weeks)',
        'Control implementation (4-8 weeks)',
        'Ongoing compliance monitoring (quarterly)'
      ],
      commonMistakes: [
        'Not identifying which state laws apply',
        'Ignoring transparency requirements',
        'Not implementing human review for AI decisions',
        'Missing bias testing requirements'
      ],
      resourceLinks: [
        { title: "HHS HIPAA and AI", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/index.html", type: "external" }
      ],
            timeToComplete: {
        small: '6-10 weeks',
        medium: '10-16 weeks',
        large: '16-24 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Legal team', 'Compliance Officer', 'AI system administrators', 'State regulatory contacts'],
      whereToLook: ['State regulations', 'AI system documentation', 'Patient consent processes', 'Bias testing reports'],
      howToVerify: 'Identify which states you operate in. Review applicable AI laws. Assess current compliance gaps. Implement transparency, human review, and bias testing requirements.',
      whatToDocument: 'State law applicability matrix, compliance gaps, transparency disclosures, human review processes, bias testing results'
    }
  },
];
